import {
  StorageKeys,
  readStorage,
  removeStorage,
  writeStorage,
} from "../auth/browserStorage";
import { AffiliateResolution, AffiliateTheme, ResolvedAffiliate } from "./types";

export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;
export const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REQUEST_TIMEOUT_MS = 4000;

/**
 * Affiliate slug from ?affiliate=, remembered for the tab so it survives the multi-step sign-up and
 * password-reset flows and the trip to /logged-out. An invalid value in the URL is ignored.
 *
 * Only the sign-in pages read the remembered value: the app itself brands by hostname, or by an
 * ?affiliate= that is in the address bar right now. Otherwise a slug used once to preview an
 * affiliate would keep branding every page for the rest of the session.
 */
export function getAffiliateSlug(
  search: string = window.location.search,
  options: { includeStored?: boolean } = {}
): string | undefined {
  const { includeStored = true } = options;

  const fromUrl = new URLSearchParams(search).get("affiliate")?.trim().toLowerCase();
  if (fromUrl && SLUG_PATTERN.test(fromUrl)) {
    writeStorage("session", StorageKeys.affiliateSlug, fromUrl);
    return fromUrl;
  }

  if (!includeStored) {
    return undefined;
  }

  const stored = readStorage("session", StorageKeys.affiliateSlug);
  return stored && SLUG_PATTERN.test(stored) ? stored : undefined;
}

const THEME_CACHE_PREFIX = "affiliate.theme:";

const cacheKey = (hostname: string, slug?: string) =>
  `${THEME_CACHE_PREFIX}${hostname.toLowerCase()}:${slug ?? ""}`;

/**
 * The affiliate resolved on a previous page load in this tab. Read synchronously during the first
 * render so a returning visitor never sees product branding flash before theirs arrives.
 *
 * Storage is untrusted input, so the cached theme goes through the same validation as a fresh one.
 */
export function readCachedAffiliate(
  hostname: string,
  slug: string | undefined,
  cdnBaseUrl: string
): ResolvedAffiliate | null {
  const raw = readStorage("session", cacheKey(hostname, slug));
  if (!raw) {
    return null;
  }

  try {
    const cached = JSON.parse(raw) as Partial<ResolvedAffiliate>;
    const theme = parseAffiliateTheme(cached.theme, cdnBaseUrl);

    if (!theme || typeof cached.slug !== "string" || typeof cached.brandingUrl !== "string") {
      return null;
    }

    return {
      slug: cached.slug,
      name: typeof cached.name === "string" ? cached.name : theme.name,
      version: typeof cached.version === "number" ? cached.version : 0,
      brandingUrl: cached.brandingUrl,
      theme,
    };
  } catch {
    return null;
  }
}

function cacheAffiliate(hostname: string, slug: string | undefined, affiliate: ResolvedAffiliate): void {
  writeStorage("session", cacheKey(hostname, slug), JSON.stringify(affiliate));
}

function clearCachedAffiliate(hostname: string, slug: string | undefined): void {
  removeStorage("session", cacheKey(hostname, slug));
}

function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/** Branding assets must come from the configured CDN (or at least https when none is configured). */
function isBrandingAssetUrl(value: unknown, cdnBaseUrl: string): value is string {
  if (cdnBaseUrl) {
    return typeof value === "string" && value.startsWith(`${cdnBaseUrl}/`) && !value.includes("..");
  }
  return isHttpsUrl(value);
}

function isText(value: unknown, maxLength: number): value is string {
  return typeof value === "string" && value.length <= maxLength;
}

/** Validates an untrusted theme.json document. Every value is rendered as text or an image URL only. */
export function parseAffiliateTheme(value: unknown, cdnBaseUrl: string): AffiliateTheme | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const theme = value as Record<string, unknown>;

  if (!isText(theme.name, 100) || theme.name.trim() === "") return null;
  if (!isBrandingAssetUrl(theme.logoUrl, cdnBaseUrl)) return null;
  if (theme.faviconUrl != null && !isBrandingAssetUrl(theme.faviconUrl, cdnBaseUrl)) return null;
  if (typeof theme.primary !== "string" || !HEX_COLOR_PATTERN.test(theme.primary)) return null;
  if (typeof theme.background !== "string" || !HEX_COLOR_PATTERN.test(theme.background)) return null;
  if (typeof theme.text !== "string" || !HEX_COLOR_PATTERN.test(theme.text)) return null;
  if (theme.tagline != null && !isText(theme.tagline, 200)) return null;
  if (!isHttpsUrl(theme.termsUrl)) return null;
  if (
    theme.supportEmail != null &&
    !(isText(theme.supportEmail, 254) && EMAIL_PATTERN.test(theme.supportEmail))
  ) {
    return null;
  }

  return {
    name: theme.name.trim(),
    logoUrl: theme.logoUrl,
    faviconUrl: (theme.faviconUrl as string | null | undefined) ?? null,
    primary: theme.primary,
    background: theme.background,
    text: theme.text,
    tagline: (theme.tagline as string | null | undefined) ?? "",
    termsUrl: theme.termsUrl,
    supportEmail: (theme.supportEmail as string | null | undefined) ?? null,
  };
}

async function fetchJson(
  url: string,
  init: RequestInit,
  fetchImpl: typeof fetch
): Promise<{ status: number; body?: unknown }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetchImpl(url, { ...init, signal: controller.signal });
    if (!response.ok) {
      return { status: response.status };
    }
    return { status: response.status, body: await response.json() };
  } finally {
    clearTimeout(timer);
  }
}

export interface ResolveAffiliateOptions {
  hostname: string;
  slug?: string;
  apiUrl: string;
  cdnBaseUrl: string;
  skipHosts: string[];
  fetchImpl?: typeof fetch;
}

/**
 * Resolves the page's affiliate and loads its branding. Any failure yields kind "none", and the
 * caller falls back to the Microsoft-hosted sign-in page; this function never throws.
 */
export async function resolveAffiliate(options: ResolveAffiliateOptions): Promise<AffiliateResolution> {
  const fetchImpl = options.fetchImpl ?? ((input, init) => window.fetch(input, init));
  const hostname = options.hostname.toLowerCase();
  const includeHost = !options.skipHosts.includes(hostname);

  if (!includeHost && !options.slug) {
    return { kind: "none", reason: "skipped" };
  }
  if (!options.apiUrl) {
    return { kind: "none", reason: "resolve-failed" };
  }

  const params = new URLSearchParams();
  if (includeHost) params.set("host", hostname);
  if (options.slug) params.set("affiliate", options.slug);

  let resolved: { status: number; body?: unknown };
  try {
    resolved = await fetchJson(
      `${options.apiUrl}/api/v1/affiliates/resolve?${params.toString()}`,
      { credentials: "omit" },
      fetchImpl
    );
  } catch {
    return { kind: "none", reason: "resolve-failed" };
  }

  if (resolved.status === 404) {
    // The affiliate was suspended or removed: stop branding from the stale cache.
    clearCachedAffiliate(hostname, options.slug);
    return { kind: "none", reason: "not-found" };
  }

  const body = resolved.body as
    | { slug?: unknown; name?: unknown; brandingUrl?: unknown; version?: unknown; theme?: unknown }
    | undefined;

  if (
    !body ||
    typeof body.slug !== "string" ||
    !isBrandingAssetUrl(body.brandingUrl, options.cdnBaseUrl)
  ) {
    return { kind: "none", reason: "resolve-failed" };
  }

  let theme: AffiliateTheme | null;

  if (body.theme != null) {
    // The API sends the theme inline, so a branded page costs one request rather than two.
    theme = parseAffiliateTheme(body.theme, options.cdnBaseUrl);
    if (!theme) {
      return { kind: "none", reason: "theme-invalid" };
    }
  } else {
    // Older API: fetch the document the branding URL points at. Versioned URLs are immutable,
    // so the HTTP cache can serve them.
    let themeResponse: { status: number; body?: unknown };
    try {
      themeResponse = await fetchJson(
        body.brandingUrl,
        { credentials: "omit", cache: "force-cache" },
        fetchImpl
      );
    } catch {
      return { kind: "none", reason: "theme-failed" };
    }

    if (themeResponse.body === undefined) {
      return { kind: "none", reason: "theme-failed" };
    }

    theme = parseAffiliateTheme(themeResponse.body, options.cdnBaseUrl);
    if (!theme) {
      return { kind: "none", reason: "theme-invalid" };
    }
  }

  const affiliate: ResolvedAffiliate = {
    slug: body.slug,
    name: typeof body.name === "string" ? body.name : theme.name,
    version: typeof body.version === "number" ? body.version : 0,
    brandingUrl: body.brandingUrl,
    theme,
  };

  cacheAffiliate(hostname, options.slug, affiliate);
  return { kind: "found", affiliate };
}
